import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { billingApi } from '../services/billingApi';
import type { EobImagePageDto } from '../types/billing';

const PAGE_SIZE = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kib = bytes / 1024;
  if (kib < 1024) return `${kib.toFixed(1)} KiB`;
  const mib = kib / 1024;
  if (mib < 1024) return `${mib.toFixed(1)} MiB`;
  return `${(mib / 1024).toFixed(2)} GiB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}Z`;
}

const EobArchive: React.FC = () => {
  const [page, setPage] = useState<EobImagePageDto | null>(null);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openingImageId, setOpeningImageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const loadPage = useCallback(async (cursor: string | null, q?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await billingApi.listEobImages(cursor, PAGE_SIZE, q);
      setPage(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load EOB archive';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setCursorStack([null]);
      loadPage(null, searchQuery || undefined);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery, loadPage]);

  const handleNext = () => {
    if (!page?.nextCursor) return;
    setCursorStack((stack) => [...stack, page.nextCursor!]);
    loadPage(page.nextCursor, searchQuery || undefined);
  };

  const handlePrev = () => {
    setCursorStack((stack) => {
      if (stack.length <= 1) return stack;
      const next = stack.slice(0, -1);
      loadPage(next[next.length - 1], searchQuery || undefined);
      return next;
    });
  };

  const handleOpen = async (imageId: string) => {
    setOpeningImageId(imageId);
    try {
      const { url } = await billingApi.getEobImageViewUrl(imageId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open EOB';
      setError(message);
    } finally {
      setOpeningImageId(null);
    }
  };

  const currentPageIndex = cursorStack.length - 1;
  const startRow = currentPageIndex * PAGE_SIZE;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: '#162B51' }}>
        EOB archive
      </Typography>
      <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
        Athena EOB image export - scanned paper EOBs received by the practice. Sorted by Athena image ID.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Couldn&apos;t load archive</AlertTitle>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by patient, payer, check #, claim ID..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        inputProps={{ 'aria-label': 'search EOBs' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, width: 400 }}
      />

      <Paper sx={{ p: 2 }}>
        {page && (
          <Typography variant="body2" sx={{ color: '#616161', mb: 2 }}>
            Showing {page.items.length === 0 ? 0 : startRow + 1}
            –{startRow + page.items.length} of {page.totalCount}
          </Typography>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Image ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payer</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Paid Total</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Skeleton variant="rectangular" height={200} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && page && page.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#9e9e9e' }}>
                    <InboxOutlinedIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">No EOB images.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && page && page.items.map((row) => (
                <TableRow key={row.imageId} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.imageId}</TableCell>
                  <TableCell align="right">{formatBytes(row.sizeBytes)}</TableCell>
                  <TableCell>{row.payerName ?? ''}</TableCell>
                  <TableCell align="right">
                    {row.paidTotal != null
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.paidTotal)
                      : ''}
                  </TableCell>
                  <TableCell>{formatDate(row.lastModified)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleOpen(row.imageId)}
                      disabled={openingImageId === row.imageId}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            onClick={handlePrev}
            disabled={isLoading || cursorStack.length <= 1}
          >
            Previous
          </Button>
          <Button
            onClick={handleNext}
            disabled={isLoading || !page?.nextCursor}
            variant="contained"
          >
            Next
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EobArchive;
